import React, { useEffect, useMemo, useState } from "react";
import { fmtDate, fmtDateTime } from "@/lib/dateUtils";
import { DateField } from "@/components/ui/DateField";
import { usePager, Pager } from "@/components/ui/TablePager";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Video, Trash2, Download, PlayCircle, X, Search, Radio, Pause, Play, RefreshCw, Share2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Recordings page — table format with search filters and a Play modal that
 * can optionally broadcast synchronised playback to a live class (Phase 2).
 *
 * Filters: keyword (title/subject/topic), date range (from/to), month, subject.
 * Broadcast: when the teacher hits "Cast to class", play/pause/seek events are
 * relayed via /api/live/{classId}/share (overlay type = "recording") so every
 * student on that class page (both authenticated /live/:id and guest /guest/:id)
 * sees the same video in lock-step.
 */
export default function RecordingsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(null); // recording currently playing in modal
  const [teacherClasses, setTeacherClasses] = useState([]); // for broadcast target
  const [broadcastClassId, setBroadcastClassId] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);

  // Search & filter state
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [subject, setSubject] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/classes/recordings/all");
      setItems(data.recordings || []);
    } catch { toast.error("Could not load recordings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Load teacher's classes once for the broadcast target dropdown
  useEffect(() => {
    if (user?.role !== "teacher") return;
    api.get("/classes")
      .then((r) => setTeacherClasses(r?.data?.classes || []))
      .catch(() => {});
  }, [user]);

  const del = async (id) => {
    if (!window.confirm("Delete this recording permanently?")) return;
    try { await api.delete(`/classes/${id}/recording`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Public share link — parents watch in the browser, no login needed.
  const share = async (c) => {
    try {
      const { data } = await api.post(`/classes/${c.id}/recording/share`);
      const url = `${window.location.origin}${data.path}`;
      await navigator.clipboard.writeText(url);
      toast.success("Shareable link copied — send it to parents, it plays right in the browser", { duration: 6000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create share link"); }
  };

  const [syncing, setSyncing] = useState(false);
  const syncMeet = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/classes/meet-recordings/sync");
      if (data.synced) toast.success(`Synced ${data.synced} new Google Meet recording${data.synced > 1 ? "s" : ""}`);
      else toast.info("No new Meet recordings yet — they appear a few minutes after a recorded class ends");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const [selected, setSelected] = useState([]);
  const toggleSel = (id) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} recording${selected.length > 1 ? "s" : ""} permanently?`)) return;
    const res = await Promise.allSettled(selected.map((id) => api.delete(`/classes/${id}/recording`)));
    const ok = res.filter((r) => r.status === "fulfilled").length;
    toast.success(`Deleted ${ok} recording${ok === 1 ? "" : "s"}`);
    setSelected([]);
    load();
  };

  // Distinct subject list for the subject filter dropdown
  const subjects = useMemo(() => {
    const s = new Set();
    items.forEach((r) => r.subject && s.add(r.subject));
    return Array.from(s).sort();
  }, [items]);

  // Word-by-word case-insensitive keyword filter over title, subject, topic
  // and the recording filename. Every token in the query must match at least
  // one of the fields so users can search "algebra oct" or "1a chapter 3".
  const filtered = useMemo(() => {
    const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return items.filter((r) => {
      if (subject && r.subject !== subject) return false;
      if (dateFrom && r.start_time && r.start_time.slice(0, 10) < dateFrom) return false;
      if (dateTo && r.start_time && r.start_time.slice(0, 10) > dateTo) return false;
      if (tokens.length === 0) return true;
      const hay = [
        r.title, r.subject, r.topic, r.chapter, r.notes,
        r.recording_url ? r.recording_url.split("/").pop() : "",
        r.start_time,
      ].join(" ").toLowerCase();
      return tokens.every((t) => hay.includes(t));
    }).sort((a, b) => (b.start_time || "").localeCompare(a.start_time || ""));
  }, [items, q, dateFrom, dateTo, subject]);

  const { page, setPage, pages, pageItems, total } = usePager(filtered, 25);
  const allChecked = filtered.length > 0 && filtered.every((r) => selected.includes(r.id));
  const toggleAll = () => setSelected(allChecked ? [] : filtered.map((r) => r.id));

  const resetFilters = () => { setQ(""); setDateFrom(""); setDateTo(""); setSubject(""); };

  // ─── Playback broadcast (Phase 2) ─────────────────────────────────────────
  // When teacher hits play/pause/seek in the modal, relay the state to the
  // selected class so all students (enrolled + guest via public poll) see the
  // same video position in lock-step.
  const castState = async (rec, videoEl, force = false) => {
    if ((!force && !broadcasting) || !broadcastClassId || !rec || !videoEl) return;
    try {
      await api.post(`/live/${broadcastClassId}/share`, {
        type: "recording",
        ref_id: rec.id,
        payload: {
          title: rec.title,
          recording_url: rec.recording_url,
          recording_id: rec.id,
          current_time: videoEl.currentTime || 0,
          playing: !videoEl.paused,
          duration: videoEl.duration || 0,
          updated_at: Date.now(),
        },
      });
    } catch { /* silent — retry on next event */ }
  };

  const stopBroadcast = async () => {
    if (!broadcastClassId) { setBroadcasting(false); return; }
    try { await api.post(`/live/${broadcastClassId}/dismiss`); } catch { /* noop */ }
    setBroadcasting(false);
    toast("Stopped broadcasting to class");
  };

  const startBroadcast = async () => {
    if (!broadcastClassId) { toast.error("Choose a class first"); return; }
    setBroadcasting(true);
    // Fire the first cast immediately (force=true bypasses the broadcasting
    // flag check since the state hasn't propagated yet) so guests see the
    // video even if the browser blocks autoplay (they'd otherwise wait until
    // the next play / pause / seek event to catch up).
    setTimeout(() => castState(opened, document.querySelector('[data-testid="rec-video"]'), true), 100);
    toast.success("Broadcasting — students will see this recording in their class window");
  };

  return (
    <div className="space-y-6" data-testid="recordings-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1">/ recordings</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2">
            <Video size={26} /> Class recordings
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            {user?.role === "teacher" && "Google Meet recordings sync here automatically after class — or pull them now with the sync button."}
            {user?.role === "student" && "Re-watch your past classes any time."}
            {user?.role === "parent" && "Watch what your child learned this week."}
            {user?.role === "institute" && "Recordings from every teacher in your institute."}
          </p>
        </div>
        {user?.role === "teacher" && (
          <button
            onClick={syncMeet}
            disabled={syncing}
            data-testid="sync-meet-recordings-btn"
            className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-md bg-[#00897B] hover:bg-[#00A48F] text-white transition-colors disabled:opacity-60"
            title="Fetch new Google Meet recordings from Drive"
          >
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync Meet recordings"}
          </button>
        )}
      </div>

      {/* Search + filter bar */}
      <div className="bg-white border border-black/8 rounded-2xl p-3 sm:p-4 flex flex-col lg:flex-row lg:items-end gap-3">
        <div className="flex-1 min-w-0">
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1 block">Keyword search</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Try &quot;algebra chapter 3&quot; or a topic name"
              data-testid="rec-search"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-neutral-300 text-sm focus:outline-none focus:border-[#0A0C10]"
            />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1 block">From</label>
          <DateField value={dateFrom} onChange={setDateFrom} testid="rec-date-from" className="px-3 py-2 rounded-md border border-neutral-300 text-sm bg-white" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1 block">To</label>
          <DateField value={dateTo} onChange={setDateTo} testid="rec-date-to" className="px-3 py-2 rounded-md border border-neutral-300 text-sm bg-white" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1 block">Subject</label>
          <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="rec-subject" className="px-3 py-2 rounded-md border border-neutral-300 text-sm bg-white focus:outline-none focus:border-[#0A0C10]">
            <option value="">All subjects</option>
            {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {(q || dateFrom || dateTo || subject) && (
          <button onClick={resetFilters} data-testid="rec-reset" className="text-xs px-3 py-2 rounded-md border border-neutral-300 hover:border-[#0A0C10] text-neutral-600">
            Reset
          </button>
        )}
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-400 lg:ml-auto">
          {loading ? "loading" : `${filtered.length} of ${items.length}`}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-black/15 rounded-2xl p-12 text-center text-sm text-neutral-500">
          {items.length === 0 ? "No recordings yet." : "No recordings match those filters."}
        </div>
      ) : (
        <div className="bg-white border border-black/8 rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid="rec-table">
            <thead className="bg-[#FBFAF7] text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">
              <tr>
                {user?.role === "teacher" && <th className="px-4 py-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="rec-select-all" /></th>}
                <th className="px-5 py-3 text-left">Title</th>
                <th className="px-5 py-3 text-left">Subject</th>
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Size</th>
                <th className="px-5 py-3 text-right">
                  {user?.role === "teacher" && selected.length > 0 ? (
                    <button onClick={bulkDelete} data-testid="rec-bulk-delete"
                      className="inline-flex items-center gap-1 text-[10px] font-bold normal-case tracking-normal border border-red-300 text-red-600 hover:bg-red-50 px-2 py-1 rounded-md">
                      <Trash2 size={11} /> Delete ({selected.length})
                    </button>
                  ) : "Actions"}
                </th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((c) => (
                <tr key={c.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`rec-${c.id}`}>
                  {user?.role === "teacher" && (
                    <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSel(c.id)} data-testid={`rec-check-${c.id}`} /></td>
                  )}
                  <td className="px-5 py-3">
                    <div className="font-display font-semibold text-sm truncate max-w-[280px] inline-flex items-center gap-1.5">
                      {c.title || "Untitled recording"}
                      {c.recording_source === "google_meet" && (
                        <span data-testid={`rec-meet-badge-${c.id}`} className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#00897B]/10 text-[#00897B] border border-[#00897B]/30 shrink-0">Meet</span>
                      )}
                    </div>
                    {c.topic && <div className="text-xs text-neutral-500 truncate max-w-[280px]">{c.topic}</div>}
                  </td>
                  <td className="px-5 py-3 text-neutral-600 whitespace-nowrap">{c.subject || "—"}</td>
                  <td className="px-5 py-3 text-neutral-600 whitespace-nowrap text-xs font-mono">{fmtDateTime(c.start_time) || "—"}</td>
                  <td className="px-5 py-3 text-neutral-500 whitespace-nowrap text-xs font-mono">{c.recording_size_mb ? `${c.recording_size_mb} MB` : "—"}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => setOpened(c)}
                        data-testid={`rec-play-${c.id}`}
                        title="Play"
                        className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#0A0C10] text-[#FBFAF7] hover:bg-black inline-flex items-center gap-1.5"
                      >
                        <PlayCircle size={13} /> Play
                      </button>
                      {user?.role === "teacher" && (
                        <button
                          onClick={() => share(c)}
                          data-testid={`rec-share-${c.id}`}
                          title="Copy a shareable link for parents (plays in the browser, no login)"
                          className="text-xs font-semibold px-3 py-1.5 rounded-md bg-[#22D3EE]/15 text-[#0E7490] border border-[#22D3EE]/40 hover:bg-[#22D3EE]/30 inline-flex items-center gap-1.5"
                        >
                          <Share2 size={12} /> Share
                        </button>
                      )}
                      <a
                        href={`${BACKEND}${c.recording_url}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Download"
                        data-testid={`rec-download-${c.id}`}
                        className="text-xs text-neutral-500 hover:text-[#0A0C10] border border-neutral-300 hover:border-neutral-500 rounded-md p-1.5"
                      >
                        <Download size={13} />
                      </a>
                      {c.drive_link && (
                        <a
                          href={c.drive_link}
                          target="_blank"
                          rel="noreferrer"
                          title="Open in Google Drive"
                          data-testid={`rec-drive-${c.id}`}
                          className="text-xs font-semibold text-emerald-600 border border-emerald-300 hover:bg-emerald-50 rounded-md px-2 py-1.5"
                        >
                          Drive
                        </a>
                      )}
                      {user?.role === "teacher" && (
                        <button
                          onClick={() => del(c.id)}
                          data-testid={`del-rec-${c.id}`}
                          title="Delete"
                          className="text-xs text-red-500 border border-red-200 hover:bg-red-50 rounded-md p-1.5"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pages={pages} total={total} onPage={setPage} testid="rec-pager" />
        </div>
      )}

      {opened && (
        <PlaybackModal
          rec={opened}
          onClose={async () => { if (broadcasting) await stopBroadcast(); setOpened(null); }}
          user={user}
          teacherClasses={teacherClasses}
          broadcastClassId={broadcastClassId}
          setBroadcastClassId={setBroadcastClassId}
          broadcasting={broadcasting}
          startBroadcast={startBroadcast}
          stopBroadcast={stopBroadcast}
          castState={castState}
        />
      )}
    </div>
  );
}


function PlaybackModal({ rec, onClose, user, teacherClasses, broadcastClassId, setBroadcastClassId, broadcasting, startBroadcast, stopBroadcast, castState }) {
  const videoRef = React.useRef(null);
  const src = `${BACKEND}${rec.recording_url}`;

  // Relay play/pause/seek events to the class so students see synced playback.
  const onPlay = () => castState(rec, videoRef.current);
  const onPause = () => castState(rec, videoRef.current);
  const onSeeked = () => castState(rec, videoRef.current);

  const isTeacher = user?.role === "teacher";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="rec-playback-modal">
      <div className="bg-[#0A0C10] w-full max-w-4xl rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 flex items-center justify-between text-white">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-400">/ playback</div>
            <div className="font-display font-semibold">{rec.title}</div>
            <div className="text-xs text-neutral-400 mt-0.5">{rec.subject} · {fmtDateTime(rec.start_time)}</div>
          </div>
          <button onClick={onClose} data-testid="rec-close" className="text-white p-2 hover:bg-white/10 rounded-md"><X size={18} /></button>
        </div>
        <video
          ref={videoRef}
          src={src}
          controls
          autoPlay
          onPlay={onPlay}
          onPause={onPause}
          onSeeked={onSeeked}
          className="w-full bg-black"
          data-testid="rec-video"
        />
        {isTeacher && (
          <div className="px-5 py-3 bg-[#10141C] border-t border-white/5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500 mb-1">Cast to a class</div>
              <select
                value={broadcastClassId}
                onChange={(e) => setBroadcastClassId(e.target.value)}
                disabled={broadcasting}
                data-testid="rec-cast-class"
                className="w-full px-3 py-2 rounded-md bg-black text-white border border-white/15 text-sm disabled:opacity-60"
              >
                <option value="">Select a class to broadcast to…</option>
                {teacherClasses.map((c) => (
                  <option key={c.id} value={c.id}>{c.title} · {c.subject} · {fmtDate(c.start_time)}</option>
                ))}
              </select>
            </div>
            {broadcasting ? (
              <button
                onClick={stopBroadcast}
                data-testid="rec-broadcast-stop"
                className="text-sm font-semibold px-4 py-2 rounded-md bg-red-500/20 border border-red-400/50 text-red-200 hover:bg-red-500/30 inline-flex items-center gap-2"
              >
                <Pause size={14} /> Stop broadcast
              </button>
            ) : (
              <button
                onClick={startBroadcast}
                disabled={!broadcastClassId}
                data-testid="rec-broadcast-start"
                className="text-sm font-semibold px-4 py-2 rounded-md bg-[#FFD600] text-[#0A0C10] disabled:opacity-40 hover:bg-[#FFE04A] inline-flex items-center gap-2"
              >
                <Radio size={14} /> Broadcast to class
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
